/**
 * Results Page
 * Display paired results with rankings and export functionality
 */

import { getTimestamps, formatTime, updateTimestampTime, parseTime } from '../lib/timing.js'
import { getAthleteByBib, loadRoster, getSelectedRace, getAgeGroupPreset, getAthleteAgeGroup } from '../lib/storage.js'
import { getBibEntries, updateBibEntry } from './chute.js'
import { getAthleteName, generateCSV } from '../lib/csv.js'
import { getCurrentRole } from '../lib/access.js'

// Filter state
let currentFilter = 'all' // 'all', 'M', 'F', 'team'
let currentAgeGroupFilter = 'all' // 'all' or specific age group name
let editingResultId = null // Position of result being edited

/**
 * Get paired and ranked results
 */
function getResults() {
  const timestamps = getTimestamps()
  const bibEntries = getBibEntries()
  const selectedRace = getSelectedRace()
  const results = []

  const maxLen = Math.max(timestamps.length, bibEntries.length)

  for (let i = 0; i < maxLen; i++) {
    const timestamp = timestamps[i] || null
    const entry = bibEntries[i] || null
    const athlete = entry?.bib ? getAthleteByBib(entry.bib) : null

    // Include if we have a time and entry is not skipped (unknown is OK)
    if (timestamp && entry && !entry.skipped) {
      // Determine age group - use athlete's assigned group or calculate from race preset
      let calculatedAgeGroup = athlete?.ageGroup || null
      if (!calculatedAgeGroup && athlete && selectedRace) {
        calculatedAgeGroup = getAthleteAgeGroup(athlete, selectedRace)
      }

      results.push({
        position: i + 1,
        bib: entry.bib,
        unknown: entry.unknown || false,
        time: timestamp.netTime,
        athlete,
        firstName: athlete?.firstName || '',
        lastName: athlete?.lastName || '',
        gender: athlete?.gender || null,
        grade: athlete?.grade || null,
        age: athlete?.age || null,
        ageGroup: calculatedAgeGroup,
        division: athlete?.division || null,
        team: athlete?.team || null
      })
    }
  }

  // Sort by time (already should be in order, but ensure)
  results.sort((a, b) => a.time - b.time)

  // Calculate overall rank
  results.forEach((r, idx) => {
    r.overallRank = idx + 1
  })

  // Calculate gender ranks
  const maleResults = results.filter(r => r.gender === 'M')
  const femaleResults = results.filter(r => r.gender === 'F')

  maleResults.forEach((r, idx) => {
    r.genderRank = idx + 1
  })

  femaleResults.forEach((r, idx) => {
    r.genderRank = idx + 1
  })

  // For those without gender, no gender rank
  results.filter(r => !r.gender).forEach(r => {
    r.genderRank = null
  })

  // Calculate age group ranks (within gender)
  const ageGroups = [...new Set(results.map(r => r.ageGroup).filter(Boolean))]
  ageGroups.forEach(ag => {
    // Male in this age group
    const maleInAG = results.filter(r => r.ageGroup === ag && r.gender === 'M')
    maleInAG.forEach((r, idx) => {
      r.ageGroupRank = idx + 1
    })

    // Female in this age group
    const femaleInAG = results.filter(r => r.ageGroup === ag && r.gender === 'F')
    femaleInAG.forEach((r, idx) => {
      r.ageGroupRank = idx + 1
    })
  })

  // Those without age group get no age group rank
  results.filter(r => !r.ageGroup).forEach(r => {
    r.ageGroupRank = null
  })

  return results
}

/**
 * Calculate team scores (XC style: Top 5 score, 6 & 7 displace)
 */
function calculateTeamScores(results) {
  const teamData = {}

  // Group by team
  results.forEach(r => {
    if (!r.team || r.unknown) return
    if (!teamData[r.team]) {
      teamData[r.team] = {
        name: r.team,
        runners: [],
        score: 0,
        scorers: [],
        displacers: []
      }
    }
    teamData[r.team].runners.push(r)
  })

  const teams = []
  for (const name in teamData) {
    const team = teamData[name]
    // Teams must have at least 5 runners to score
    if (team.runners.length < 5) continue

    // Sort runners by time (using overall rank for points)
    team.runners.sort((a, b) => a.time - b.time)

    // Calculate score from top 5
    for (let i = 0; i < Math.min(team.runners.length, 7); i++) {
      const runner = team.runners[i]
      if (i < 5) {
        team.score += runner.overallRank
        team.scorers.push(runner)
      } else {
        team.displacers.push(runner)
      }
    }

    teams.push(team)
  }

  // Sort teams by score (lowest first)
  teams.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score

    // Tie-break: 6th runner position
    const a6 = a.displacers[0]?.overallRank || 9999
    const b6 = b.displacers[0]?.overallRank || 9999
    return a6 - b6
  })

  // Assign ranks
  teams.forEach((t, idx) => {
    t.rank = idx + 1
  })

  return teams
}

/**
 * Get statistics
 */
function getStats(results) {
  const ageGroups = [...new Set(results.map(r => r.ageGroup).filter(Boolean))].sort((a, b) => {
    // Basic alphabetical sort for now, better than random
    return String(a).localeCompare(String(b))
  })

  return {
    total: results.length,
    male: results.filter(r => r.gender === 'M').length,
    female: results.filter(r => r.gender === 'F').length,
    unknown: results.filter(r => r.unknown).length,
    teams: [...new Set(results.map(r => r.team).filter(Boolean))].length,
    ageGroups,
    timestampsRecorded: getTimestamps().length,
    bibsEntered: getBibEntries().filter(e => !e.skipped).length
  }
}

/**
 * Filter results by gender and age group
 */
function filterResults(results, genderFilter, ageGroupFilter) {
  let filtered = results

  if (genderFilter !== 'all') {
    filtered = filtered.filter(r => r.gender === genderFilter)
  }

  if (ageGroupFilter !== 'all') {
    filtered = filtered.filter(r => r.ageGroup === ageGroupFilter)
  }

  return filtered
}

/**
 * Render the Results page
 */
export function renderResultsPage(container, onNavigate) {
  const allResults = getResults()
  const stats = getStats(allResults)
  const filteredResults = filterResults(allResults, currentFilter, currentAgeGroupFilter)
  const selectedRace = getSelectedRace()

  container.innerHTML = `
    <div class="results-page">
      <header class="results-header">
        <h1>Results</h1>
        <div class="header-actions">
          <button class="btn-live" id="viewLiveBtn">
            View Live
          </button>
          <button class="btn-export" id="exportBtn" ${allResults.length === 0 ? 'disabled' : ''}>
            Export CSV
          </button>
        </div>
      </header>

      <!-- Stats Bar -->
      <div class="stats-bar">
        <div class="stat-item">
          <span class="stat-value">${stats.total}</span>
          <span class="stat-label">Finishers</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">${stats.male}</span>
          <span class="stat-label">Male</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">${stats.female}</span>
          <span class="stat-label">Female</span>
        </div>
        ${stats.unknown > 0 ? `
          <div class="stat-item stat-warning">
            <span class="stat-value">${stats.unknown}</span>
            <span class="stat-label">Pending ID</span>
          </div>
        ` : ''}
        <div class="stat-item">
          <span class="stat-value">${stats.teams}</span>
          <span class="stat-label">Teams</span>
        </div>
      </div>

      <!-- Filter Tabs -->
      <div class="filter-tabs">
        <button class="filter-tab ${currentFilter === 'all' ? 'active' : ''}" data-filter="all">
          All (${allResults.length})
        </button>
        <button class="filter-tab ${currentFilter === 'M' ? 'active' : ''}" data-filter="M">
          Male (${stats.male})
        </button>
        <button class="filter-tab ${currentFilter === 'F' ? 'active' : ''}" data-filter="F">
          Female (${stats.female})
        </button>
        ${selectedRace?.raceType === 'xc' ? `
          <button class="filter-tab ${currentFilter === 'team' ? 'active' : ''}" data-filter="team">
            Team Results
          </button>
        ` : ''}
      </div>

        <!-- Age Group Filter -->
        <div class="age-group-filter">
          <label for="ageGroupSelect">Category:</label>
          <div class="select-wrapper">
            <select id="ageGroupSelect">
              <option value="all" ${currentAgeGroupFilter === 'all' ? 'selected' : ''}>All Categories</option>
              ${stats.ageGroups.map(ag => `
                <option value="${ag}" ${currentAgeGroupFilter === ag ? 'selected' : ''}>${ag}</option>
              `).join('')}
            </select>
          </div>
        </div>

      <!-- Results Table / Team Table -->
      <div class="results-table-container">
        ${currentFilter === 'team' ? renderTeamTable(allResults) : `
          ${filteredResults.length > 0 ? `
            <table class="results-table">
              <thead>
                <tr>
                  <th class="col-rank">Rank</th>
                  ${currentFilter === 'all' && currentAgeGroupFilter === 'all' ? '<th class="col-gender-rank">G</th>' : ''}
                  <th class="col-bib">Bib</th>
                  <th class="col-name">Name</th>
                  <th class="col-time">Time</th>
                  <th class="col-gender">G</th>
                  <th class="col-age-group">AG</th>
                  <th class="col-team">Team</th>
                </tr>
              </thead>
              <tbody>
                ${filteredResults.map((r) => {
    let displayRank = r.overallRank
    if (currentFilter !== 'all' && currentAgeGroupFilter !== 'all') {
      displayRank = r.ageGroupRank
    } else if (currentFilter !== 'all') {
      displayRank = r.genderRank
    } else if (currentAgeGroupFilter !== 'all') {
      displayRank = r.ageGroupRank
    }

    const isPodium = displayRank <= 3 && !r.unknown
    const podiumClass = isPodium ? `podium podium-${displayRank}` : ''
    const canEdit = true  // All users can edit in simplified mode

    return `
                    <tr class="${podiumClass} ${r.unknown ? 'unknown-row' : ''} ${canEdit ? 'editable-row' : ''}" data-position="${r.position}">
                      <td class="col-rank">
                        ${displayRank || '-'}
                        ${isPodium ? '<span class="medal-icon"></span>' : ''}
                      </td>
                      ${currentFilter === 'all' && currentAgeGroupFilter === 'all' ? `<td class="col-gender-rank">${r.genderRank || '-'}</td>` : ''}
                      <td class="col-bib ${r.unknown ? 'unknown' : ''}">
                        ${r.unknown ? 'UNK' : r.bib}
                        ${canEdit ? '<span class="edit-hint">✎</span>' : ''}
                      </td>
                      <td class="col-name ${r.unknown ? 'unknown' : ''}">${r.unknown ? 'Pending ID' : r.athlete ? getAthleteName(r.athlete) : `Bib #${r.bib}`}</td>
                      <td class="col-time">${formatTime(r.time)}</td>
                      <td class="col-gender">${r.gender || '-'}</td>
                      <td class="col-age-group">${r.ageGroup || '-'}</td>
                      <td class="col-team">${r.team || '-'}</td>
                    </tr>
                  `
  }).join('')}
              </tbody>
            </table>
          ` : `
            <div class="no-results">
              <p>No results yet.</p>
              <p class="text-secondary">
                ${stats.timestampsRecorded === 0 ? 'Record finish times in the Tapper.' : ''}
                ${stats.bibsEntered === 0 ? 'Enter bib numbers in the Chute.' : ''}
                ${stats.timestampsRecorded > 0 && stats.bibsEntered > 0 ? 'Waiting for paired data...' : ''}
              </p>
            </div>
          `}
        `}
      </div>

      <!-- Status Footer -->
      <div class="status-footer">
        <span class="status-item">
          <span class="status-dot ${stats.timestampsRecorded > stats.bibsEntered ? 'warning' : 'ok'}"></span>
          ${stats.timestampsRecorded} times recorded
        </span>
        <span class="status-item">
          <span class="status-dot ${stats.bibsEntered > stats.timestampsRecorded ? 'warning' : 'ok'}"></span>
          ${stats.bibsEntered} bibs entered
        </span>
      </div>
    </div>
  `

  attachResultsHandlers(container, onNavigate, allResults)

  if (editingResultId !== null) {
    renderEditModal(container, allResults.find(r => r.position === editingResultId), onNavigate)
  }
}

/**
 * Attach event handlers
 */
function attachResultsHandlers(container, onNavigate, allResults) {
  // Filter tabs
  container.querySelectorAll('.filter-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      currentFilter = tab.dataset.filter
      renderResultsPage(container, onNavigate)
    })
  })

  // Age group filter
  document.getElementById('ageGroupSelect')?.addEventListener('change', (e) => {
    currentAgeGroupFilter = e.target.value
    renderResultsPage(container, onNavigate)
  })

  // View Live button
  document.getElementById('viewLiveBtn')?.addEventListener('click', () => {
    onNavigate('live')
  })

  // Export button
  document.getElementById('exportBtn')?.addEventListener('click', () => {
    exportResults(allResults)
  })

  // Editable rows
  container.querySelectorAll('.editable-row').forEach(row => {
    row.addEventListener('click', () => {
      editingResultId = parseInt(row.dataset.position, 10)
      renderResultsPage(container, onNavigate)
    })
  })
}

/**
 * Render Edit Result Modal
 */
function renderEditModal(container, result, onNavigate) {
  if (!result) return

  const modal = document.createElement('div')
  modal.className = 'modal-overlay'
  modal.innerHTML = `
    <div class="modal edit-result-modal">
      <header class="modal-header">
        <h2>Edit Result #${result.position}</h2>
        <button class="btn-close" id="closeModalBtn">×</button>
      </header>
      
      <div class="modal-body">
        <div class="form-group">
          <label>Bib Number</label>
          <input type="number" id="editBib" value="${result.unknown ? '' : result.bib}" placeholder="Enter bib #" />
          ${result.unknown ? '<p class="text-secondary small">Currently marked as unknown</p>' : ''}
        </div>
        
        <div class="athlete-preview" id="athletePreview">
          ${result.athlete ? `
            <div class="preview-card">
              <span class="preview-name">${getAthleteName(result.athlete)}</span>
              <span class="preview-details">${result.athlete.team || 'No Team'}</span>
            </div>
          ` : '<p class="text-secondary small">Enter a bib to see athlete info</p>'}
        </div>

        <div class="form-group">
          <label>Finish Time</label>
          <input type="text" id="editTime" value="${formatTime(result.time)}" />
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" id="cancelEditBtn">Cancel</button>
        <button class="btn-primary" id="saveEditBtn">Save Changes</button>
      </div>
    </div>
  `
  document.body.appendChild(modal)

  const close = () => {
    modal.remove()
    editingResultId = null
  }

  document.getElementById('closeModalBtn').addEventListener('click', close)
  document.getElementById('cancelEditBtn').addEventListener('click', close)

  // Bib lookup
  document.getElementById('editBib').addEventListener('input', (e) => {
    const bib = e.target.value
    const athlete = getAthleteByBib(bib)
    const preview = document.getElementById('athletePreview')
    if (athlete) {
      preview.innerHTML = `
        <div class="preview-card">
          <span class="preview-name">${getAthleteName(athlete)}</span>
          <span class="preview-details">${athlete.team || 'No Team'}</span>
        </div>
      `
    } else {
      preview.innerHTML = '<p class="text-secondary small">Athlete not found in roster</p>'
    }
  })

  // Save changes
  document.getElementById('saveEditBtn').addEventListener('click', () => {
    const newBib = parseInt(document.getElementById('editBib').value, 10)
    const newTimeStr = document.getElementById('editTime').value
    const newTimeMs = parseTime(newTimeStr)

    // 1. Update Bib Entry in Chute
    updateBibEntry(result.position, newBib)

    // 2. Update Time in Timing Engine
    const timestamps = getTimestamps()
    const ts = timestamps.find(t => t.sequence === result.position)
    if (ts && newTimeMs !== null) {
      updateTimestampTime(ts.id, newTimeMs)
    }

    close()
    renderResultsPage(container, onNavigate)
  })
}

/**
 * Export results to CSV
 */
function exportResults(results) {
  if (results.length === 0) return

  // Build CSV content
  const headers = ['Place', 'Bib', 'First Name', 'Last Name', 'Time', 'Gender', 'Grade', 'Age Group', 'Division', 'Team', 'Gender Place']
  const rows = [headers.join(',')]

  results.forEach(r => {
    const row = [
      r.overallRank,
      r.bib,
      escapeCSV(r.firstName),
      escapeCSV(r.lastName),
      formatTimeForExport(r.time),
      r.gender || '',
      r.grade || '',
      escapeCSV(r.ageGroup || ''),
      escapeCSV(r.division || ''),
      escapeCSV(r.team || ''),
      r.genderRank || ''
    ]
    rows.push(row.join(','))
  })

  const csvContent = rows.join('\n')

  // Create download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  const date = new Date().toISOString().split('T')[0]
  link.setAttribute('href', url)
  link.setAttribute('download', `race-results-${date}.csv`)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

/**
 * Format time for export (MM:SS.ss)
 */
function formatTimeForExport(ms) {
  if (ms == null) return ''
  const totalSeconds = ms / 1000
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`
}

/**
 * Escape CSV value
 */
function escapeCSV(value) {
  if (!value) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

/**
 * Get results page styles
 */
/**
 * Render Team Table
 */
function renderTeamTable(allResults) {
  const teams = calculateTeamScores(allResults)

  if (teams.length === 0) {
    return `
      <div class="no-results">
        <p>Not enough runners for team scores.</p>
        <p class="text-secondary">Teams need at least 5 finishers to be ranked.</p>
      </div>
    `
  }

  return `
    <table class="results-table team-table">
      <thead>
        <tr>
          <th class="col-rank">Rank</th>
          <th class="col-team">Team</th>
          <th class="col-score">Score</th>
          <th class="col-scorers">Scorers (1st-5th)</th>
          <th class="col-displacers">Displacers (6th-7th)</th>
        </tr>
      </thead>
      <tbody>
        ${teams.map(t => `
          <tr class="${t.rank <= 3 ? 'podium podium-' + t.rank : ''}">
            <td class="col-rank">${t.rank}</td>
            <td class="col-team">${t.name}</td>
            <td class="col-score">${t.score}</td>
            <td class="col-scorers">
              <div class="scorer-list">
                ${t.scorers.map(s => `<span class="scorer-dot" title="${getAthleteName(s)} (#${s.bib})">${s.overallRank}</span>`).join('')}
              </div>
            </td>
            <td class="col-displacers">
              <div class="scorer-list displace">
                ${t.displacers.map(s => `<span class="scorer-dot" title="${getAthleteName(s)} (#${s.bib})">${s.overallRank}</span>`).join('')}
              </div>
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `
}
